import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import LoadingState from '../components/LoadingState';
import valerieImage from '../images/valerie_accolade.png';

export default function ResetPasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { session, loading: authLoading, updatePassword } = useAuth();
  const navigate = useNavigate();

  // Show loading state while determining authentication status
  if (authLoading) {
    return <LoadingState />;
  }

  // Redirect to login if not authenticated (user needs to be logged in via the reset link)
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    const { error } = await updatePassword(newPassword);
    setLoading(false);

    if (error) {
      setError(error.message || 'Failed to update password.');
    } else {
      setSuccess(true);
      // Redirect to home after successful password reset
      setTimeout(() => {
        navigate('/');
      }, 2000);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen w-full bg-white text-zinc-900 flex">
        {/* Left panel */}
        <div className="flex-1 flex items-center justify-center px-6 sm:px-12 lg:px-20 bg-gray-50 shadow-lg">
          <div className="w-full max-w-md">
            {/* Logo */}
            <div className="mb-10">
              <img src="/logo_horizontal.png" alt="NoteWell" className="h-8 w-auto" />
            </div>

            <h1 className="text-3xl font-semibold">Password updated!</h1>
            <p className="mt-2 text-sm text-zinc-500">
              Your password has been successfully updated. Redirecting you to the app...
            </p>

            <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-700">
                Password updated successfully! You will be redirected to the app shortly.
              </p>
            </div>
          </div>
        </div>

        {/* Right panel: quote */}
        <aside className="hidden xl:flex flex-[1.5] items-center justify-center p-10 bg-white">
          <div className="max-w-lg text-zinc-700">
            <p className="text-3xl font-medium leading-snug text-zinc-900">
              "Notewell lets my caseworkers <span className="font-bold">review, not write</span>. It
              keeps our clients front and center and lightens the paperwork load so my team can
              actually breathe."
            </p>
            <div className="mt-6 flex items-center gap-3">
              <img src={valerieImage} alt="Valerie H." className="h-14 w-14 rounded-full" />
              <div>
                <p className="text-sm text-zinc-800">Valerie H.</p>
                <p className="text-xs text-zinc-600">Executive Director</p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-white text-zinc-900 flex">
      {/* Left panel */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 lg:px-20 bg-gray-50 shadow-lg">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-10">
            <img src="/logo_horizontal.png" alt="NoteWell" className="h-8 w-auto" />
          </div>

          <h1 className="text-3xl font-semibold">Reset your password</h1>
          <p className="mt-2 text-sm text-zinc-500">Enter your new password below</p>

          {/* Password form */}
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            {error && (
              <div className="text-red-500 text-sm text-center mb-2">
                {error.split('\n').map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
            )}

            <div>
              <label htmlFor="newPassword" className="block text-sm mb-1">
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm placeholder-zinc-400 focus:border-emerald-500 focus:ring-emerald-500"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm mb-1">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm placeholder-zinc-400 focus:border-emerald-500 focus:ring-emerald-500"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-4 w-full rounded-md bg-indigo-600 py-2.5 text-sm font-medium text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating password…' : 'Update password'}
            </button>
          </form>

          {/* Legal */}
          <p className="mt-10 text-[11px] leading-5 text-zinc-400 text-center">
            By continuing, you agree to Notewell&apos;s{' '}
            <a
              href="https://www.notewell.ai/terms-of-service"
              className="underline hover:text-zinc-700"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://www.notewell.ai/privacy-policy"
              className="underline hover:text-zinc-700"
            >
              Privacy Policy
            </a>
            , and to receive periodic emails with updates.
          </p>
        </div>
      </div>

      {/* Right panel: quote */}
      <aside className="hidden xl:flex flex-[1.5] items-center justify-center p-10 bg-white">
        <div className="max-w-lg text-zinc-700">
          <p className="text-3xl font-medium leading-snug text-zinc-900">
            "Notewell lets my caseworkers <span className="font-bold">review, not write</span>. It
            keeps our clients front and center and lightens the paperwork load so my team can
            actually breathe."
          </p>
          <div className="mt-6 flex items-center gap-3">
            <img src={valerieImage} alt="Valerie H." className="h-14 w-14 rounded-full" />
            <div>
              <p className="text-sm text-zinc-800">Valerie H.</p>
              <p className="text-xs text-zinc-600">Executive Director</p>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
